import React from 'react';

const cardShadow = { boxShadow: '10px 13px 0px #898db740' };

interface CardProps {
  badge: string;
  badgeLeft: string;
  num: string;
  title: string;
  bullets: { bold: string; text: string }[];
  icon: string;
}

const Card: React.FC<CardProps> = ({ badge, badgeLeft, num, title, bullets, icon }) => (
  <div className="bg-ebony-clay border-2 border-wistful rounded overflow-hidden p-6 lg:p-8 pb-24 lg:pb-28 relative" style={cardShadow}>
    <div className="relative w-[54px] h-[52px] mb-4 lg:mb-6 scale-75 origin-top-left lg:scale-100">
      <img className="w-[52px] h-[52px]" src={badge} alt="" />
      <div className="absolute top-[5px] font-exo font-bold text-white text-2xl lg:text-[32px] tracking-[1.6px] leading-normal" style={{ left: badgeLeft }}>{num}</div>
    </div>
    <h3 className="relative z-10 font-proxima-bold font-bold text-white text-lg lg:text-xl tracking-[1px] leading-normal mb-4">
      {title}
    </h3>
    <div className="relative z-10 space-y-3 lg:space-y-4">
      {bullets.map((b, i) => (
        <p key={i} className="font-proxima text-white text-sm lg:text-base tracking-[0.8px] leading-normal">
          <span className="font-proxima-bold font-bold">{b.bold}</span>
          <span>{b.text}</span>
        </p>
      ))}
    </div>
    {/* Decorative circles */}
    <div className="absolute -right-20 -bottom-20 w-[200px] lg:w-[253px] h-[200px] lg:h-[253px] pointer-events-none">
      <div className="absolute inset-0 rounded-full bg-royal-blue opacity-[0.05]" />
      <div className="absolute left-4 lg:left-6 top-4 lg:top-6 w-[160px] lg:w-[206px] h-[160px] lg:h-[206px] rounded-full bg-royal-blue opacity-[0.15]" />
      <div className="absolute left-8 lg:left-12 top-8 lg:top-12 w-[120px] lg:w-[157px] h-[120px] lg:h-[157px] rounded-full bg-royal-blue opacity-[0.25]" />
    </div>
    {/* Corner icon */}
    <img className="absolute right-7 bottom-7 lg:right-10 lg:bottom-10 w-7 h-7 lg:w-10 lg:h-10 object-contain z-0 pointer-events-none" src={icon} alt="" />
  </div>
);

const HowItWorksSection: React.FC = () => {
  const circleGradient = 'radial-gradient(circle at 50% 50%, rgba(93,101,161,1) 0%, rgba(34,37,59,1) 50%)';

  return (
    <section className="py-16 lg:py-24 relative overflow-hidden" style={{ background: circleGradient }}>
      {/* Radial ellipse decoration */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(50% 50% at 50% 50%, rgba(34,37,59,0.45) 0%, rgba(181,186,222,0) 100%)',
        }}
      />

      {/* Logo watermarks — hidden on mobile */}
      <div className="absolute left-[-162px] top-[25%] w-[609px] h-[570px] opacity-[1] hidden lg:block pointer-events-none">
        <img className="absolute left-0 top-[20.44%] w-[18.89%] h-[35.07%]" src={require('../assets/img/how-it-works/logo-l-1.png')} alt="" />
        <img className="absolute left-[19.45%] top-0 w-[53.99%] h-[49.67%]" src={require('../assets/img/how-it-works/logo-l-2.svg').default} alt="" />
        <img className="absolute left-[44.89%] top-[35.35%] w-[55.11%] h-[51.28%]" src={require('../assets/img/how-it-works/logo-l-3.svg').default} alt="" />
        <img className="absolute left-[44.70%] top-[78.78%] w-[36.12%] h-[21.22%]" src={require('../assets/img/how-it-works/logo-l-4.svg').default} alt="" />
        <img className="absolute left-[60.51%] top-0 w-[32.14%] h-[33.17%]" src={require('../assets/img/how-it-works/logo-l-5.svg').default} alt="" />
        <img className="absolute left-[10.72%] top-[31.95%] w-[21.52%] h-[54.58%]" src={require('../assets/img/how-it-works/logo-l-6.svg').default} alt="" />
      </div>
      <div className="absolute right-[-162px] top-[25%] w-[609px] h-[570px] opacity-[1] hidden lg:block pointer-events-none">
        <img className="absolute left-0 top-[20.44%] w-[18.89%] h-[35.07%]" src={require('../assets/img/how-it-works/logo-r-1.svg').default} alt="" />
        <img className="absolute left-[19.45%] top-0 w-[53.90%] h-[49.67%]" src={require('../assets/img/how-it-works/logo-r-2.svg').default} alt="" />
        <img className="absolute left-[44.89%] top-[35.35%] w-[28.46%] h-[51.28%]" src={require('../assets/img/how-it-works/logo-r-3.svg').default} alt="" />
        <img className="absolute left-[44.70%] top-[78.78%] w-[28.65%] h-[21.22%]" src={require('../assets/img/how-it-works/logo-r-4.svg').default} alt="" />
        <img className="absolute left-[60.51%] top-0 w-[12.84%] h-[33.17%]" src={require('../assets/img/how-it-works/logo-r-5.svg').default} alt="" />
        <img className="absolute left-[10.72%] top-[31.95%] w-[37.39%] h-[54.58%]" src={require('../assets/img/how-it-works/logo-r-6.svg').default} alt="" />
      </div>

      <div className="max-w-content mx-auto px-6 relative z-10">
        <h2 className="font-exo font-bold text-white text-2xl lg:text-[38px] tracking-[1.9px] leading-normal text-center mb-4">
          How Metalogix Works
        </h2>
        <p className="font-proxima text-white text-sm lg:text-base tracking-[0.8px] leading-normal text-center mb-10 lg:mb-12">
          For New Projects or Existing Systems
        </p>

        {/* 2x2 card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-[820px] mx-auto">
          <Card
            badge={require('../assets/img/how-it-works/badge-1.svg').default}
            icon={require('../assets/img/how-we-work/preferred-method.svg').default}
            badgeLeft="20px" num="1"
            title="Choose your preferred method"
            bullets={[
              { bold: 'Book a Meeting: ', text: 'Talk directly with our consultants' },
              { bold: 'Use Our AI Assistant:', text: ' Get instant feedback 24/7' },
              { bold: 'Submit Documents: ', text: 'Upload existing specs or briefs' },
            ]}
          />
          <Card
            badge={require('../assets/img/how-it-works/badge-2.svg').default}
            icon={require('../assets/img/how-we-work/design.svg').default}
            badgeLeft="17px" num="2"
            title="Design & Architecture Phase"
            bullets={[
              { bold: 'UX/UI Design: ', text: 'Wireframes, mockups, and journey maps' },
              { bold: 'Functional Specifications', text: ': Detailed feature documentation' },
              { bold: 'Style Guides: ', text: 'Branding guidelines and design system' },
              { bold: 'Technical Architecture', text: ': Database, API, and infrastructure' },
            ]}
          />
          <Card
            badge={require('../assets/img/how-it-works/badge-3.svg').default}
            icon={require('../assets/img/how-we-work/build.svg').default}
            badgeLeft="17px" num="3"
            title="Build Your Solution"
            bullets={[
              { bold: 'Dedicated Project Owner:', text: ' Your personal contact for updates' },
              { bold: 'Smart Development:', text: ' Advanced tools for faster delivery' },
              { bold: 'Personal Demo & Testing: ', text: 'Hands-on demonstrations' },
              { bold: 'Full Transparency: ', text: 'Access to code and project board' },
            ]}
          />
          <Card
            badge={require('../assets/img/how-it-works/badge-4.svg').default}
            icon={require('../assets/img/how-we-work/fix.svg').default}
            badgeLeft="16px" num="4"
            title="Fix, Optimize & Perfect"
            bullets={[
              { bold: 'Bug Fixing', text: ': Comprehensive testing and issue resolution' },
              { bold: 'Performance Tuning: ', text: 'Optimize for speed and scalability' },
              { bold: 'Security Hardening: ', text: 'Enterprise-grade security' },
              { bold: 'Final Polish: ', text: 'UI/UX refinements' },
            ]}
          />
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
